import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { CreditCard, AlertCircle, CheckCircle2, Clock, Wallet, Receipt, Building2, Copy, Landmark } from "lucide-react";

import AxiosDB from "@/Config/AxiosDB";

const fmt = (n) => new Intl.NumberFormat("es-DO", { style: "currency", currency: "DOP" }).format(n || 0);

const MESES = ["", "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const periodoLabel = (p) => (p && p.length >= 6 ? `${MESES[parseInt(p.substring(4, 6), 10)]} ${p.substring(0, 4)}` : p);

const BANCOS = [
  { id: "banreservas-corriente", banco: "BanReservas", tipo: "Cuenta Corriente", numero: "1202116075", titular: "D ANGEL SOFTWARE ELECTRONIC SRL", moneda: "RD$" },

  { id: "popular", banco: "Banco Popular", tipo: "Cuenta Corriente", numero: "826715021", titular: "ANGEL CASTILLO", moneda: "RD$" },
  { id: "bhd", banco: "BHD", tipo: "Cuenta de Ahorro", numero: "31998710011", titular: "ANGEL CASTILLO", moneda: "RD$" },
];

export default function Facturacion({ user, onLogout }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pagando, setPagando] = useState(null);
  const [pagoFactura, setPagoFactura] = useState(null); // factura seleccionada para pagar
  const [bancoSel, setBancoSel] = useState("banreservas-corriente");


  const load = async () => {
    setLoading(true);
    try {
      const res = await AxiosDB.get(`${API}/facturacion`);
      setData(res.data);
    } catch (e) {
      toast.error("Error al cargar la facturación");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const abrirPago = (factura) => {
    setBancoSel("banreservas-corriente");
    setPagoFactura(factura);
  };

  const handlePagar = async (id) => {
    setPagando(id);
    try {
      await AxiosDB.post(`${API}/facturacion/${id}/pagar`, { pagoFactura, banco: BANCOS.filter(r => r.id === bancoSel)[0] });
      toast.success("Confirmacion de Pago Enviado exitosamente");
      setPagoFactura(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al procesar el pago");
    } finally {
      setPagando(null);
    }
  };

  const estadoBadge = (estado) => {
    if (estado === "pagada") return <Badge className="bg-green-600"><CheckCircle2 className="w-3 h-3 mr-1" /> {estado}</Badge>;
    if (estado === "vencida") return <Badge className="bg-red-600"><AlertCircle className="w-3 h-3 mr-1" /> {estado}</Badge>;
    return <Badge className="bg-amber-500"><Clock className="w-3 h-3 mr-1" /> {estado}</Badge>;
  };

  const facturas = data?.facturas || [];
  const pendientes = data?.pendientes || [];

  const historial = data?.pagadas || [];

  return (
    <Layout user={user} onLogout={onLogout} title="Facturación">
      {loading ? (
        <p className="text-slate-500 py-8 text-center">Cargando...</p>
      ) : (
        <div className="space-y-6">
          {/* Resumen */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card data-testid="resumen-pendiente">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardDescription>Total Pendiente</CardDescription>
                  <div className="p-2 rounded-lg bg-amber-50"><Wallet className="w-5 h-5 text-amber-600" /></div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-amber-600" style={{ fontFamily: "Space Grotesk, sans-serif" }}>
                  {fmt(data?.resumen?.total_pendiente)}
                </div>
                <p className="text-sm text-slate-500 mt-1">{data?.resumen?.cantidad_pendiente || 0} factura(s) por pagar</p>
              </CardContent>
            </Card>
            <Card data-testid="resumen-pagado">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardDescription>Total Pendiente por Confirmar</CardDescription>
                  <div className="p-2 rounded-lg bg-green-50"><CheckCircle2 className="w-5 h-5 text-green-600" /></div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-green-600" style={{ fontFamily: "Space Grotesk, sans-serif" }}>
                  {fmt(data?.resumen?.total_pendiente_confirmar)}
                </div>
                <p className="text-sm text-slate-500 mt-1">{data?.resumen?.cantidad_total_pendiente_confirmar || 0} factura(s) pagada(s)</p>
              </CardContent>
            </Card>
            <Card data-testid="resumen-total">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardDescription>Total Facturas</CardDescription>
                  <div className="p-2 rounded-lg bg-blue-50"><Receipt className="w-5 h-5 text-blue-600" /></div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-slate-800" style={{ fontFamily: "Space Grotesk, sans-serif" }}>
                  {facturas.length}
                </div>
                <p className="text-sm text-slate-500 mt-1">Registros históricos</p>
              </CardContent>
            </Card>
          </div>

          {/* Facturas Pendientes */}
          <Card data-testid="pendientes-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-amber-500" /> Facturas Pendientes de Pago
              </CardTitle>
              <CardDescription>Facturas de tu suscripción que requieren pago.</CardDescription>
            </CardHeader>
            <CardContent>
              {pendientes.length === 0 ? (
                <div className="text-center py-8 text-slate-500" data-testid="pendientes-empty">
                  <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-green-400" />
                  <p>¡Estás al día! No tienes facturas pendientes.</p>
                </div>
              ) : (
                <div className="rounded-lg border border-slate-200 overflow-hidden">
                  <Table data-testid="pendientes-table">
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>No. Factura</TableHead>
                        <TableHead>Fecha</TableHead>
                        <TableHead>Concepto</TableHead>
                        <TableHead>Vencimiento</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead className="text-right">Balance</TableHead>
                        <TableHead className="text-center">Estado</TableHead>
                        <TableHead className="text-center">Acción</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pendientes.map((f) => (
                        <TableRow key={f.id} data-testid={`pendiente-row-${f.periodo}`}>
                          <TableCell className="font-mono text-sm">{f.numero}</TableCell>
                          <TableCell>{f.fecha}</TableCell>

                          <TableCell>{f.concepto}</TableCell>
                          <TableCell>{f.fecha_vencimiento}</TableCell>
                          <TableCell className="text-right font-semibold">{fmt(f.total)}</TableCell>
                          <TableCell className="text-right font-semibold">{fmt(f.balance)}</TableCell>
                          <TableCell className="text-center">{estadoBadge(f.estado)}</TableCell>
                          <TableCell className="text-center">
                            <Button size="sm" onClick={() => abrirPago(f)} disabled={pagando === f.id || f.estado !== 'Pendiente'}
                              className="bg-blue-600 hover:bg-blue-700" data-testid={`pagar-${f.periodo}`}>
                              <CreditCard className="w-4 h-4 mr-1" /> {pagando === f.id ? "Procesando..." : "Pagar"}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Historial de Facturas */}
          <Card data-testid="historial-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="w-5 h-5 text-blue-600" /> Historial de Facturas
              </CardTitle>
              <CardDescription>Todas las facturas emitidas de tu suscripción.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg border border-slate-200 overflow-hidden">
                <Table data-testid="historial-table">
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>No. Factura</TableHead>
                      <TableHead>Fecha</TableHead>
                      <TableHead>Concepto</TableHead>
                      <TableHead>Vencimiento</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead>Fecha Pago</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {historial.map((f) => (
                      <TableRow key={f.id} data-testid={`pendiente-row-${f.periodo}`}>
                        <TableCell className="font-mono text-sm">{f.numero}</TableCell>
                        <TableCell>{f.fecha}</TableCell>

                        <TableCell>{f.concepto}</TableCell>
                        <TableCell>{f.fecha_vencimiento}</TableCell>
                        <TableCell className="text-right font-semibold">{fmt(f.total)}</TableCell>
                        <TableCell>{f.fecha_pago || "—"}</TableCell>

                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Diálogo de instrucciones de pago */}
      <Dialog open={!!pagoFactura} onOpenChange={(o) => !o && setPagoFactura(null)}>
        <DialogContent data-testid="pago-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Landmark className="w-5 h-5 text-blue-600" /> Instrucciones de Pago
            </DialogTitle>
            <DialogDescription>
              Realiza un depósito o transferencia por <strong>{fmt(pagoFactura?.total)}</strong> a una de nuestras cuentas y luego confirma el pago.
            </DialogDescription>
          </DialogHeader>

          {pagoFactura && (
            <div className="space-y-4 py-1">
              <div>
                <Label className="text-sm mb-1 block">Selecciona el banco</Label>
                <Select value={bancoSel} onValueChange={setBancoSel}>
                  <SelectTrigger data-testid="banco-select">
                    <SelectValue placeholder="Elige un banco para ver la cuenta" />
                  </SelectTrigger>
                  <SelectContent>
                    {BANCOS.map((b) => (
                      <SelectItem key={b.id} value={b.id} data-testid={`banco-option-${b.id}`}>
                        {b.banco} — {b.tipo}{b.moneda === "US$" ? " (US$)" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {(() => {
                const b = BANCOS.find((x) => x.id === bancoSel);
                if (!b) {
                  return (
                    <div className="text-center text-slate-500 py-6 border border-dashed rounded-lg" data-testid="pago-sin-banco">
                      <Building2 className="w-10 h-10 mx-auto mb-2 text-slate-400" />
                      <p className="text-sm">Selecciona un banco para ver las instrucciones de depósito.</p>
                    </div>
                  );
                }
                const concepto = `Pago Factura ${pagoFactura.numero}`;
                const Fila = ({ label, value, copyable, testId }) => (
                  <div className="flex items-start justify-between gap-3 py-2 border-b border-slate-100 last:border-0">
                    <span className="text-sm text-slate-500">{label}</span>
                    <span className="text-sm font-semibold text-slate-800 text-right flex items-center gap-2" data-testid={testId}>
                      {value}
                      {copyable && (
                        <button onClick={() => copiar(value)} className="text-blue-600 hover:text-blue-800" title="Copiar">
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </span>
                  </div>
                );
                return (
                  <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-4" data-testid="pago-instrucciones">
                    <div className="flex items-center gap-2 mb-2">
                      <Building2 className="w-5 h-5 text-blue-700" />
                      <span className="font-bold text-slate-800">{b.banco}</span>
                      <Badge variant="secondary">{b.tipo}</Badge>
                    </div>
                    <Fila label="No. de Cuenta" value={b.numero} copyable testId="pago-numero-cuenta" />
                    <Fila label="A nombre de" value={b.titular} testId="pago-titular" />
                    <Fila label="Moneda" value={b.moneda === "US$" ? "Dólares (US$)" : "Pesos (RD$)"} />
                    <Fila label="Monto a depositar" value={fmt(pagoFactura.total)} testId="pago-monto" />
                    <Fila label="Concepto" value={concepto} copyable testId="pago-concepto" />
                  </div>
                );
              })()}

              <p className="text-xs text-slate-500">
                Importante: utiliza exactamente el concepto indicado para identificar tu pago. Una vez realizado el depósito, presiona "Ya realicé el pago".
              </p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setPagoFactura(null)}>Cancelar</Button>
            <Button
              onClick={() => handlePagar(pagoFactura.id)}
              disabled={!bancoSel || pagando === pagoFactura?.id}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirmar-pago-button"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              {pagando === pagoFactura?.id ? "Procesando..." : "Ya realicé el pago"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
